import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircleIcon, ArrowRightIcon, ShieldCheckIcon, MapPinIcon, CheckCircle2Icon, ChevronDownIcon, StarIcon, ClockIcon, CheckIcon } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { useLanguage } from '../hooks/useLanguage';
import { NightCollaborations } from '../components/NightCollaborations';
export function IbizaNightDriver() {
  const {
    language
  } = useLanguage();
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  // SEO meta tags
  useEffect(() => {
    document.title = 'Ibiza Night Driver | Fixed-Price Night Transport for Clubs, Dinners & Return Trips';
    const metaDesc = document.querySelector('meta[name="description"]');
    const descContent = 'Book a night driver in Ibiza for dinners, clubs, events and return pickups. Fixed prices, local drivers, premium vehicles and fast WhatsApp booking.';
    if (metaDesc) {
      metaDesc.setAttribute('content', descContent);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = descContent;
      document.head.appendChild(meta);
    }
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, [language]);
  // WhatsApp URL
  const whatsappUrl = language === 'en' ? 'https://wa.me/34613756211?text=Hello%2C%20I%20need%20a%20night%20driver%20in%20Ibiza.' : 'https://wa.me/34613756211?text=Hola%2C%20necesito%20un%20chófer%20nocturno%20en%20Ibiza.';
  // FAQs Data
  const faqs = [{
    q: 'Can I book both drop-off and return pickup?',
    a: 'Yes. We can arrange both in advance and confirm the full plan before the night starts.'
  }, {
    q: 'Do you work after midnight?',
    a: 'Yes. We provide late-night transport depending on availability and the booking details.'
  }, {
    q: 'Do you cover clubs and restaurants across Ibiza?',
    a: 'Yes. We cover the main nightlife, dining and pickup areas across the island.'
  }, {
    q: 'How much does a night transfer cost in Ibiza?',
    a: 'The price depends on route, timing and number of passengers. We always confirm the fixed price before booking.'
  }, {
    q: 'Is this service useful for villa guests going out at night?',
    a: 'Yes. Many villa guests use this service for dinner plans, club drop-offs and return journeys.'
  }, {
    q: 'Can I send the full plan on WhatsApp?',
    a: "Yes. Send pickup, destination, time and passenger count and we'll confirm the best option."
  }];
  // FAQ Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <main className="flex-grow pt-20" data-id="element-673">
      {/* 1. Hero */}
      <section className="relative min-h-[70vh] md:min-h-[85vh] flex items-center bg-navy overflow-hidden" data-id="element-674">
        <div className="absolute inset-0 z-0" data-id="element-675">
          <img src="https://cdn.magicpatterns.com/uploads/c5eUuFEyp2TsP4SmsfJFLr/foto_vip_noche.jpg" alt="Mercedes V-Class premium van parked outside a nightlife venue in Ibiza at night with ambient lighting" className="w-full h-full object-cover object-[50%_center] md:object-center opacity-70" data-id="element-676" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/80 via-navy/45 to-navy/15" data-id="element-677" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-5 w-full py-12 md:py-20" data-id="element-678">
          <div className="max-w-2xl" data-id="element-679">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-680">
              <div className="inline-flex items-center space-x-1.5 md:space-x-2 bg-white/10 backdrop-blur-sm px-3 md:px-4 py-1.5 md:py-2 rounded-full mb-6 md:mb-10 border border-white/15" data-id="element-681">
                <span className="text-gold-subtle text-xs md:text-sm" data-id="element-682">★</span>
                <span className="text-white text-[10px] md:text-xs font-semibold tracking-wide" data-id="element-683">
                  Late-night service · Fixed price upfront · Return pickups
                  available · Local drivers
                </span>
              </div>

              <h1 className="max-w-xl text-[1.75rem] md:text-6xl font-heading text-white mb-6 md:mb-10 leading-[1.15] md:leading-[1.1]" data-id="element-684">
                Ibiza Night Driver — Fixed-Price Night Transport Across Ibiza
              </h1>

              <p className="text-base md:text-xl text-white/90 mb-10 md:mb-14 leading-relaxed max-w-md md:max-w-xl" data-id="element-685">
                Private night transport in Ibiza for dinners, clubs, events and
                return journeys. Local drivers, fixed prices and fast WhatsApp
                booking.
              </p>

              <div className="flex flex-col sm:flex-row items-center gap-3 md:gap-4" data-id="element-686">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-687">
                  <WhatsAppIcon className="w-4 h-4 md:w-5 md:h-5" data-id="element-688" />
                  <span data-id="element-689">Get night transfer price on WhatsApp</span>
                </a>
                <a href="#areas" className="w-full sm:w-auto flex items-center justify-center space-x-2 text-white hover:text-gold-subtle px-6 py-3 md:py-4 text-sm md:text-base font-medium transition-colors" data-id="element-690">
                  <span data-id="element-691">See nightlife areas we cover</span>
                  <ArrowRightIcon className="w-4 h-4" data-id="element-692" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 2. Trust Bar */}
      <section className="bg-warm-cream border-y border-stone/10 py-10 md:py-14" data-id="element-693">
        <div className="mx-auto max-w-7xl px-5" data-id="element-694">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 md:gap-8" data-id="element-695">
            {[{
            text: 'Late-night service available',
            icon: ClockIcon
          }, {
            text: 'Return pickups can be pre-booked',
            icon: ArrowRightIcon
          }, {
            text: 'Fixed price confirmed upfront',
            icon: ShieldCheckIcon
          }, {
            text: 'Local drivers who know Ibiza nightlife routes',
            icon: MapPinIcon
          }, {
            text: 'Fast WhatsApp quote',
            icon: MessageCircleIcon
          }].map((item, index) => <div key={index} className="flex flex-col items-center text-center space-y-3" data-id="element-696">
                <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center" data-id="element-697">
                  <item.icon className="w-5 h-5 text-gold-subtle" data-id="element-698" />
                </div>
                <span className="text-xs md:text-sm font-medium text-slate leading-tight" data-id="element-699">
                  {item.text}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* 3. Problem / Solution */}
      <section className="py-16 md:py-24 bg-white" data-id="element-700">
        <div className="mx-auto max-w-2xl px-5 text-center" data-id="element-701">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} data-id="element-702">
            <h2 className="text-3xl md:text-4xl font-heading text-navy mb-8" data-id="element-703">
              Why night transport in Ibiza needs planning
            </h2>
            <div className="space-y-6 text-lg text-slate leading-relaxed" data-id="element-704">
              <p data-id="element-705">
                At night in Ibiza, transport becomes harder. Taxi queues get
                longer, availability drops and last-minute pricing becomes less
                predictable.
              </p>
              <p data-id="element-706">
                For dinners, clubs, events and late return journeys, pre-booked
                transport is often the easiest and most reliable option.
              </p>
              <p data-id="element-707">
                We make Ibiza night transport simple with fixed prices, local
                drivers and return pickups arranged in advance.
              </p>
              <p className="font-medium text-navy pt-4" data-id="element-708">
                Go out knowing how you'll get there — and how you'll get back.
              </p>
            </div>

            <div className="mt-8" data-id="element-709">
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-710">
                <WhatsAppIcon className="w-5 h-5" data-id="element-711" />
                <span data-id="element-712">Get exact night transfer price</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 4. Nightlife Areas */}
      <section id="areas" className="py-16 md:py-24 bg-warm-white border-y border-stone/10" data-id="element-713">
        <div className="mx-auto max-w-4xl px-5 text-center" data-id="element-714">
          <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-715">
            Night transport across Ibiza
          </h2>
          <p className="text-slate leading-relaxed mb-10 max-w-2xl mx-auto" data-id="element-716">
            We provide private night transport across Ibiza for clubs,
            restaurants, hotels, villas and return pickup points. Popular
            nightlife routes include the island's main dinner and club areas.
          </p>

          <div className="flex flex-wrap justify-center gap-3 mb-10" data-id="element-717">
            {['Pacha', 'Hï Ibiza', 'Ushuaïa', 'DC-10', 'Amnesia', 'Ibiza Town', 'Marina Botafoch', "Playa d'en Bossa", 'San Antonio'].map((area, index) => <span key={index} className="px-4 py-2 bg-white border border-stone/10 rounded-full text-sm font-medium text-navy shadow-sm" data-id="element-718">
                {area}
              </span>)}
          </div>

          <p className="text-slate font-medium mb-6" data-id="element-719">
            Need another pickup or drop-off point? Send us your route on
            WhatsApp and we'll confirm availability and fixed price.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-2 text-whatsapp hover:text-whatsapp/80 font-medium transition-colors" data-id="element-720">
            <MessageCircleIcon className="w-4 h-4" data-id="element-721" />
            <span data-id="element-722">Send night route on WhatsApp</span>
            <ArrowRightIcon className="w-3.5 h-3.5" data-id="element-723" />
          </a>
        </div>
      </section>

      {/* 5. Pricing Approach */}
      <section className="py-16 md:py-24 bg-white" data-id="element-724">
        <div className="mx-auto max-w-3xl px-5" data-id="element-725">
          <div className="text-center mb-12" data-id="element-726">
            <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-727">
              Night transfer prices
            </h2>
            <p className="text-slate leading-relaxed max-w-2xl mx-auto" data-id="element-728">
              Night transport pricing depends on route, time, number of
              passengers and whether you need one-way or return service.
            </p>
          </div>

          <div className="bg-warm-white border border-stone/10 rounded-2xl p-8 md:p-10 shadow-sm text-center max-w-xl mx-auto" data-id="element-729">
            <p className="text-navy font-semibold mb-6" data-id="element-730">
              For the fastest fixed quote, send us:
            </p>
            <ul className="space-y-4 text-left max-w-xs mx-auto mb-8" data-id="element-731">
              {['pickup location', 'destination', 'time', 'number of passengers'].map((item, i) => <li key={i} className="flex items-center space-x-3" data-id="element-732">
                  <CheckIcon className="w-5 h-5 text-gold-subtle flex-shrink-0" data-id="element-733" />
                  <span className="text-slate font-medium capitalize" data-id="element-734">
                    {item}
                  </span>
                </li>)}
            </ul>

            <p className="text-sm text-slate mb-8" data-id="element-735">
              We confirm the fixed price before booking. No surprises at the end
              of the night.
            </p>

            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20" data-id="element-736">
              <WhatsAppIcon className="w-5 h-5" data-id="element-737" />
              <span data-id="element-738">Get exact night transfer price</span>
            </a>
          </div>
        </div>
      </section>

      {/* 6. When to Use */}
      <section className="py-16 md:py-24 bg-warm-cream border-y border-stone/10" data-id="element-739">
        <div className="mx-auto max-w-7xl px-5" data-id="element-740">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-741">
            <h2 className="text-3xl md:text-4xl font-heading text-navy mb-6" data-id="element-742">
              When to book a night driver in Ibiza
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-743">
            {[{
            title: 'Dinner transfers',
            desc: 'Easy transport to and from restaurants without relying on last-minute taxis.'
          }, {
            title: 'Club drop-offs',
            desc: 'Arrive on time with pre-booked transport and no taxi queue.'
          }, {
            title: 'Return pickups after the event',
            desc: 'More reliable than searching for transport late at night.'
          }, {
            title: 'Multi-stop night plans',
            desc: 'Ideal for dinner, drinks and club combinations.'
          }, {
            title: 'Villa guests going out at night',
            desc: 'Practical transport for villa stays, especially in remote areas.'
          }, {
            title: 'Couples, groups and special occasions',
            desc: 'Flexible service for different plans and passenger counts.'
          }].map((useCase, index) => <div key={index} className="bg-white border border-stone/10 rounded-xl p-6 shadow-sm" data-id="element-744">
                <CheckCircle2Icon className="w-6 h-6 text-gold-subtle mb-3" data-id="element-745" />
                <h3 className="text-base font-semibold text-navy mb-2" data-id="element-746">
                  {useCase.title}
                </h3>
                <p className="text-slate text-sm leading-relaxed" data-id="element-747">
                  {useCase.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Why Choose Us */}
      <section className="py-14 md:py-20 bg-white" data-id="element-748">
        <div className="mx-auto max-w-7xl px-5" data-id="element-749">
          <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-750">
            <h2 className="text-2xl md:text-3xl font-heading text-navy mb-3" data-id="element-751">
              Why travellers choose our Ibiza night driver service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 mb-10" data-id="element-752">
            {[{
            title: 'Avoid taxi queues',
            desc: 'Pre-booked transport instead of waiting at busy times.'
          }, {
            title: 'Fixed price before the trip',
            desc: 'Know the price before you go out.'
          }, {
            title: 'Return pickup arranged in advance',
            desc: 'Less stress after clubs, dinners or events.'
          }, {
            title: 'Local drivers who know Ibiza nightlife routes',
            desc: 'Better coordination, easier communication and smoother pickups.'
          }, {
            title: 'Great option for groups',
            desc: 'Often more practical than splitting into separate rides.'
          }].map((feature, index) => <div key={index} className="bg-warm-cream border border-stone/10 rounded-xl p-5 shadow-sm" data-id="element-753">
                <CheckCircle2Icon className="w-5 h-5 text-gold-subtle mb-2.5" data-id="element-754" />
                <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-755">
                  {feature.title}
                </h3>
                <p className="text-slate text-sm leading-relaxed" data-id="element-756">
                  {feature.desc}
                </p>
              </div>)}
          </div>

          <div className="text-center max-w-2xl mx-auto" data-id="element-757">
            <p className="text-slate font-medium" data-id="element-758">
              For dinner plans, club nights and return trips, pre-booked
              transport is often the easiest option.
            </p>
          </div>
        </div>
      </section>

      {/* Collaborations Section */}
      <NightCollaborations whatsappUrl={whatsappUrl} data-id="element-759" />

      {/* 8. FAQs */}
      <section className="py-16 md:py-24 bg-warm-white border-y border-stone/10" data-id="element-760">
        <div className="mx-auto max-w-3xl px-5" data-id="element-761">
          <h2 className="text-3xl md:text-4xl font-heading text-navy text-center mb-12" data-id="element-762">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-763">
            {faqs.map((faq, index) => <div key={index} className="bg-white border border-stone/10 rounded-xl overflow-hidden" data-id="element-764">
                <button onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} className="w-full flex items-center justify-between p-5 md:p-6 text-left" data-id="element-765">
                  <span className="font-semibold text-navy pr-4" data-id="element-766">{faq.q}</span>
                  <ChevronDownIcon className={`w-5 h-5 text-slate flex-shrink-0 transition-transform duration-300 ${openFaqIndex === index ? 'rotate-180' : ''}`} data-id="element-767" />
                </button>
                <AnimatePresence data-id="element-768">
                  {openFaqIndex === index && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-769">
                      <div className="px-5 md:px-6 pb-5 md:pb-6 text-slate leading-relaxed" data-id="element-770">
                        {faq.a}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
        <script type="application/ld+json" dangerouslySetInnerHTML={{
        __html: JSON.stringify(faqSchema)
      }} data-id="element-771" />
      </section>

      {/* 9. Reviews */}
      <section className="py-16 md:py-24 bg-white border-b border-stone/10" data-id="element-772">
        <div className="mx-auto max-w-7xl px-5" data-id="element-773">
          <h2 className="text-3xl md:text-4xl font-heading text-navy text-center mb-12" data-id="element-774">
            What clients say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-775">
            {[{
            text: 'Perfect for our night out. The driver dropped us at dinner and picked us up later outside the club.',
            author: 'Group booking, London'
          }, {
            text: 'Much easier than dealing with taxis late at night. Fast booking and clear price.',
            author: 'Guest, Madrid'
          }, {
            text: 'We booked both the drop-off and the return. Very smooth, reliable and easy to arrange.',
            author: 'Couple booking, Paris'
          }].map((review, index) => <div key={index} className="bg-warm-cream p-8 rounded-2xl" data-id="element-776">
                <div className="flex space-x-1 mb-4" data-id="element-777">
                  {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-gold-subtle text-gold-subtle" data-id="element-778" />)}
                </div>
                <p className="text-navy font-medium mb-6 leading-relaxed" data-id="element-779">
                  "{review.text}"
                </p>
                <p className="text-sm text-slate" data-id="element-780">— {review.author}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 10. Internal Links */}
      <section className="py-14 md:py-20 bg-warm-white border-b border-stone/10" data-id="element-781">
        <div className="mx-auto max-w-4xl px-5" data-id="element-782">
          <p className="text-slate text-center mb-8" data-id="element-783">
            Need a more flexible chauffeur service or transport for a larger
            group? We also offer private driver and group transfer service
            across Ibiza.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto" data-id="element-784">
            <Link to="/ibiza-group-transfer" className="flex items-center justify-between bg-white border border-stone/10 rounded-xl px-5 py-4 hover:shadow-md hover:border-stone/20 transition-all group" data-id="element-785">
              <span className="font-medium text-navy text-sm" data-id="element-786">
                Ibiza Group Transfer
              </span>
              <ArrowRightIcon className="w-4 h-4 text-slate group-hover:text-navy transition-colors" data-id="element-787" />
            </Link>
          </div>
        </div>
      </section>

      {/* 11. Final CTA */}
      <section className="py-14 md:py-20 bg-navy relative overflow-hidden" data-id="element-788">
        <div className="relative mx-auto max-w-3xl px-5 text-center z-10" data-id="element-789">
          <h2 className="text-3xl md:text-4xl font-heading text-white mb-4 leading-tight" data-id="element-790">
            Book your Ibiza night driver now
          </h2>
          <p className="text-base text-white/70 mb-8 max-w-xl mx-auto leading-relaxed" data-id="element-791">
            Send your pickup point, destination, time and number of passengers.
            We'll confirm availability and your fixed price on WhatsApp.
          </p>
          <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center space-x-2 bg-whatsapp hover:bg-whatsapp-hover text-white px-8 py-4 rounded-full font-medium transition-all shadow-lg shadow-whatsapp/20 mb-4" data-id="element-792">
            <WhatsAppIcon className="w-5 h-5" data-id="element-793" />
            <span data-id="element-794">Get night transfer price</span>
          </a>
          <p className="text-sm text-white/50" data-id="element-795">
            Fast reply · Fixed price · No upfront payment
          </p>
        </div>
      </section>
    </main>;
}